// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { customXngageWidget, fontFamilyValues } from "@guestsupply/XDL/constants";
import { color, mergeWithCss } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";

const enum fields {
    title = "title",
    viewMoreText = "viewMoreText",
    viewMoreLink = "viewMoreLink",
    border = "border",
    fontFamily = "fontFamily",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.viewMoreLink]: string | LinkFieldValue;
        [fields.viewMoreText]: string;
        [fields.border]: "top" | "bottom" | "none";
        [fields.fontFamily]: fontFamilyValues.GOTHAM | fontFamilyValues.BODONI;
    };
    extendedStyles?: SectionTitleStyles;
}

export interface SectionTitleStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    link?: LinkPresentationProps;
    icon?: IconPresentationProps;
}

export const sectionTitleStyles: SectionTitleStyles = {};

const SectionTitle: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { viewMoreLink, viewMoreText, border, fontFamily, title } = fields;
    const [styles] = useState(() => mergeWithCss(sectionTitleStyles, extendedStyles));
    const linkUrl =
        typeof viewMoreLink === "string" ? viewMoreLink : useGetLink(viewMoreLink ?? { type: "Page", value: "" }).url;
    return (
        <StyledWrapper {...styles.wrapper} {...{ border }}>
            <Typography {...styles.title} {...{ fontFamily }}>
                {title}
            </Typography>

            {linkUrl && (
                <Link href={linkUrl} {...styles.link} aria-label={viewMoreText || translate("View More")}>
                    {viewMoreText || translate("View More")}
                </Link>
            )}
        </StyledWrapper>
    );
};
export const sectionTitleFields = [
    {
        name: fields.fontFamily,
        displayName: "Title Font Family",
        editorTemplate: "DropDownField",
        defaultValue: fontFamilyValues.BODONI,
        fieldType: "General",
        options: [
            {
                displayName: "Gotham",
                value: fontFamilyValues.GOTHAM,
            },
            {
                displayName: "Bodoni MT",
                value: fontFamilyValues.BODONI,
            },
        ],
        hideEmptyOption: true,
    },
    {
        name: fields.viewMoreText,
        displayName: "Link Text",
        editorTemplate: "TextField",
        defaultValue: "",
        fieldType: "General",
    },
    {
        name: fields.viewMoreLink,
        displayName: "Link",
        editorTemplate: "LinkField",
        fieldType: "General",
        defaultValue: { type: "Page", value: "" },
    },
    {
        name: fields.border,
        editorTemplate: "DropDownField",
        defaultValue: "none",
        fieldType: "General",
        options: [
            {
                displayName: "Top",
                value: "top",
            },
            {
                displayName: "Bottom",
                value: "bottom",
            },
            {
                displayName: "None",
                value: "none",
            },
        ],
        hideEmptyOption: true,
    },
    {
        name: fields.title,
        displayName: "Title",
        editorTemplate: "TextField",
        defaultValue: "",
        fieldType: "Translatable",
        isRequired: true,
    }
];
const widgetModule: WidgetModule = {
    component: SectionTitle,
    definition: {
        group: customXngageWidget as any,
        displayName: "Section Title",
        fieldDefinitions: sectionTitleFields as any,
    },
};

export default widgetModule;
